import { Alert } from "@allaria-tech/matter/Alert";
import { ButtonGroup } from "@allaria-tech/matter/ButtonGroup";
import { Checkbox } from "@allaria-tech/matter/Checkbox";
import { InputSelect } from "@allaria-tech/matter/InputSelect";
import { Tag } from "@allaria-tech/matter/Tag";
import { Typography } from "@allaria-tech/matter/Typography";

import PartnerConsentBanner from "./PartnerConsentBanner";
import { toTestId } from "../../app/utils";
import { ConsentCopy } from "../../i18n/partnerConsent";
import {
  ConsentComitente,
  ConsentVariant,
  PartnerConsentFlow,
} from "../../utils/partnerConsent";
import { getCompanyData } from "../../utils/companies";
import { CompanyData } from "../../types";

import styles from "./PartnerConsentPage.module.scss";

type ConsentCheckbox = CompanyData["termsAndConditions"][number];

/** The linking variant's single checkbox, which accepts no document. */
const CONFORMITY = "conformity";

/**
 * Consents the onboarding asks for that this flow does not: BDS signs the terms
 * and the platform use only. Emptying this array puts the rest back.
 */
const OMITTED_CONSENTS = ["senebiAndExteriorNotes"];

interface Props {
  flow: PartnerConsentFlow;
  copy: ConsentCopy;
  variant: ConsentVariant;
  name: string;
  comitentes: ConsentComitente[];
  /** `numero` of the comitente to link; empty until one is picked. */
  selectedComitente: string;
  onSelectComitente: (numero: string) => void;
  /** ALyC variants only, from the link: the razón social and comitente number. */
  holder: string;
  comitente: string;
  /** Keyed by checkbox name; absent counts as unticked. */
  accepted: Record<string, boolean>;
  /** Drives the type scale and where the action bar goes. */
  isMobile: boolean;
  isSubmitting: boolean;
  onToggle: (name: string) => void;
  onSubmit: () => void;
}

const PartnerConsentWelcome = ({
  flow,
  copy,
  variant,
  name,
  comitentes,
  selectedComitente,
  onSelectComitente,
  holder,
  comitente,
  accepted,
  isMobile,
  isSubmitting,
  onToggle,
  onSubmit,
}: Props) => {
  const {
    screen,
    content,
    titles,
    card,
    cardBody,
    cardContent,
    checkboxesGroup,
    checkboxRow,
    checkboxControl,
    checkboxLabel,
    checkboxLabelStatic,
    greetingGroup,
    conformityText,
  } = styles;

  const { welcome } = copy;
  const fromAlyc = variant === "INVITACION" || variant === "CONFORMIDAD";
  const account = fromAlyc && welcome.account(holder, comitente);
  const documents = flow.documents ?? {};

  const documentCheckbox = (
    name: string,
    text: string,
    url?: string,
    prefix?: string,
  ): ConsentCheckbox => {
    const linkId = `document-${name}`;
    return {
      name,
      required: true,
      label: (
        <>
          {prefix && `${prefix} `}
          {url ? <span id={linkId}>{text}</span> : text}
        </>
      ),
      links: url ? [{ id: linkId, name: text, url }] : [],
    };
  };

  // Not documents of the host's onboarding, so they live here rather than in
  // companies.tsx.
  const alycCheckboxes: Partial<Record<ConsentVariant, ConsentCheckbox[]>> = {
    INVITACION: [
      documentCheckbox(
        "terms",
        welcome.terms,
        documents.terms,
        welcome.termsPrefix,
      ),
    ],
    CONFORMIDAD: [
      documentCheckbox(
        "dataTransfer",
        welcome.dataTransfer,
        documents.dataTransfer,
      ),
      documentCheckbox(
        "managementRegulation",
        welcome.managementRegulation,
        documents.managementRegulation,
        welcome.managementRegulationPrefix,
      ),
    ],
  };

  // A partner link still opens an *Allaria* account, so it signs Allaria's own
  // paperwork. Read from the company data so the two can't drift when legal
  // reissues a document. Linking signs none of it — just the one conformity.
  const checkboxes: ConsentCheckbox[] =
    alycCheckboxes[variant] ??
    (variant === "VINCULACION"
      ? [
          {
            name: CONFORMITY,
            required: true,
            label: (
              <div className={conformityText}>
                {welcome.conformity.map((paragraph) => (
                  <p key={paragraph}>{paragraph}</p>
                ))}
              </div>
            ),
            links: [],
          },
        ]
      : getCompanyData().termsAndConditions.filter(
          ({ name: checkboxName }) => !OMITTED_CONSENTS.includes(checkboxName),
        ));

  const handleLabelClick = (
    event: React.MouseEvent<Element>,
    { links }: ConsentCheckbox,
  ) => {
    const target = event.target as Node;
    const clicked = links.find(({ id }) =>
      document.getElementById(id)?.contains(target),
    );

    if (clicked) {
      // A tab, not a download: these are read, not filed. `noreferrer` as well
      // as `noopener` because this URL carries the link token, the flow's only
      // credential, and the document host has no business seeing it.
      event.preventDefault();
      event.stopPropagation();
      window.open(clicked.url, "_blank", "noopener,noreferrer");
      return;
    }

    // A label with links isn't a toggle target: a mis-click beside a link would
    // flip a consent. Without links there is nothing to mis-click.
    if (links.length) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  const isLinking = variant === "VINCULACION";

  const missingRequired =
    (isLinking && !selectedComitente) ||
    checkboxes.some(
      ({ name: checkboxName, required }) => required && !accepted[checkboxName],
    );

  const submit = {
    text: welcome.submit,
    type: "button" as const,
    testId: "consent-submit",
    // On opening this button *is* the signature, so it goes down in flight:
    // a second click would meet a 409.
    isDisabled: missingRequired || isSubmitting,
    isLoading: isSubmitting,
    onClick: onSubmit,
  };

  return (
    <>
      <div className={screen}>
        <PartnerConsentBanner flow={flow} />

        <div className={`${content} scrollbar`}>
          <div className={titles}>
            <div className={greetingGroup}>
              <Typography type="HEADING" size={isMobile ? "M" : "XL"} tag="h1">
                {welcome.greeting(name)}
              </Typography>
              {account && (
                <Typography
                  type="HEADING"
                  size={isMobile ? "S" : "L"}
                  tag="p"
                  color="var(--typography-heading-color-emphasis)"
                >
                  {account}
                </Typography>
              )}
            </div>
            <Typography type="HEADING" size={isMobile ? "S" : "L"} tag="h2">
              {welcome.subtitle}
            </Typography>
          </div>

          <section className={card}>
            <div className={cardBody}>
              <Typography type="HEADING" size={isMobile ? "S" : "M"} tag="h3">
                {welcome.lead}
              </Typography>

              <div className={cardContent}>
                {/* Vinculación no lleva alerta: su texto entero es la
                    conformidad. Sin este guard el `false` del copy igual monta
                    la caja del Alert, que queda pintada y vacía arriba del
                    checkbox. */}
                {welcome.alert && <Alert variant="INFO">{welcome.alert}</Alert>}

                {isLinking && comitentes.length > 1 && (
                  <InputSelect
                    label={welcome.comitenteLabel}
                    placeholder={welcome.comitentePlaceholder}
                    options={comitentes.map(({ numero, nombre }) => ({
                      label: `${numero} - ${nombre}`,
                      value: numero,
                    }))}
                    value={selectedComitente}
                    disableBottomSheet={!isMobile}
                    testId="consent-comitente"
                    onChange={onSelectComitente}
                  />
                )}

                <form className={checkboxesGroup}>
                  {checkboxes.map((checkbox) => {
                    const {
                      name: checkboxName,
                      label,
                      required,
                      links,
                    } = checkbox;

                    return (
                      <div className={checkboxRow} key={checkboxName}>
                        <Checkbox
                          name={checkboxName}
                          // The card variant lays itself out; the alignment
                          // fix is for the bare checkbox's long labels.
                          variant="simple"
                          className={checkboxControl}
                          testId={toTestId(
                            typeof label === "string" ? label : checkboxName,
                          )}
                          checked={!!accepted[checkboxName]}
                          onCheckedChange={() => onToggle(checkboxName)}
                          label={
                            <span
                              className={`${checkboxLabel} ${
                                links.length ? checkboxLabelStatic : ""
                              }`}
                              id={`label-${checkboxName}`}
                              onClick={(event) =>
                                handleLabelClick(event, checkbox)
                              }
                            >
                              {label}
                            </span>
                          }
                        />
                        {!required && (
                          <Tag label={welcome.optional} variant="info" />
                        )}
                      </div>
                    );
                  })}
                </form>
              </div>
            </div>

            {/* The panel's own footer on desktop. Mobile has no panel to put a
                footer in: there the bar belongs to the screen, pinned at the
                bottom of the viewport. */}
            {!isMobile && (
              <ButtonGroup container align="right" primary={submit} />
            )}
          </section>
        </div>
      </div>

      {isMobile && <ButtonGroup container primary={submit} />}
    </>
  );
};

export default PartnerConsentWelcome;
